import {
  Code2,
  Database,
  LayoutDashboard,
  Plug,
  Sparkles,
  Workflow
} from 'lucide-react';
import React, { useRef, useState } from 'react';
import { DEMO_CAMPAIGNS } from '../../constants/demo-copy';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { useInView, usePrefersReducedMotion } from '../motion';
import { MockTab, MockTabBar, OutputPanel } from './shared';

type AccessId = 'dashboard' | 'agent' | 'mcp' | 'apis' | 'warehouse';

const ACCESS_TABS = [
  { id: 'dashboard' as AccessId, label: 'Dashboard', icon: LayoutDashboard },
  { id: 'agent' as AccessId, label: 'Agent', icon: Sparkles },
  { id: 'mcp' as AccessId, label: 'MCP', icon: Plug },
  { id: 'apis' as AccessId, label: 'APIs', icon: Code2 },
  { id: 'warehouse' as AccessId, label: 'Data warehouse', icon: Database }
];

const ConceptPanel = styled('div')`
  width: 100%;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 16px 48px rgba(14, 28, 43, 0.1);
`;

const ConceptHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 18px 20px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: ${COLORS.PRIMARY};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const ConceptDots = styled('div')`
  display: flex;
  gap: 6px;
  margin-left: auto;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #e2e8f0;
  }
`;

const ConceptBody = styled('div')`
  padding: 14px 20px 20px;
`;

const PanelStack = styled('div')`
  min-height: 168px;
`;

const DashboardPreview = styled(OutputPanel)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TrendCell = styled('div')`
  grid-column: 1 / -1;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px 6px;

  .label {
    font-size: 9px;
    color: #94a3b8;
    margin-bottom: 4px;
  }

  svg {
    display: block;
    width: 100%;
    height: 34px;
  }
`;

const MiniMetric = styled('div')`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;

  .label {
    font-size: 9px;
    color: #94a3b8;
    margin-bottom: 3px;
  }

  .value {
    font-size: 13px;
    font-weight: 700;
    color: #0e1c2b;
  }
`;

const ChatPreview = styled(OutputPanel)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Bubble = styled('div')<{ $speaker: 'user' | 'agent' }>`
  align-self: ${(p) => (p.$speaker === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 92%;
  font-size: 11px;
  line-height: 1.45;
  padding: 8px 10px;
  border-radius: ${(p) =>
    p.$speaker === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px'};
  background: ${(p) => (p.$speaker === 'user' ? COLORS.PRIMARY : '#f1f5f9')};
  color: ${(p) => (p.$speaker === 'user' ? '#fff' : '#0e1c2b')};
`;

const AgentBadge = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  margin-bottom: 4px;
`;

const McpPreview = styled(OutputPanel)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ApiPreview = styled(OutputPanel)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ApiGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ApiGroupLabel = styled('div')`
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const ApiRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #0e1c2b;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  padding: 10px;

  svg {
    color: ${COLORS.PRIMARY};
    flex-shrink: 0;
  }

  .hint {
    flex: 1;
    font-size: 10px;
    font-weight: 500;
    color: #64748b;
    line-height: 1.45;
  }
`;

type ApiGroupConfig = {
  label: string;
  icon: typeof Code2;
  hint: string;
};

const API_GROUPS: readonly ApiGroupConfig[] = [
  {
    label: 'Reporting',
    icon: Code2,
    hint: 'All your affiliate data available via API'
  },
  {
    label: 'Tracking',
    icon: Database,
    hint: 'Custom tracking and integration to your own system'
  },
  {
    label: 'Automation',
    icon: Workflow,
    hint: 'Automate common tasks and workflows'
  }
] as const;

const WarehousePreview = styled(OutputPanel)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AccessSummary = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.45;

  svg {
    color: ${COLORS.PRIMARY};
    flex-shrink: 0;
  }
`;

const PanelIntro = styled('p')<{
  visible: boolean;
  reduced: boolean;
}>`
  font-size: 11px;
  color: #64748b;
  margin: 0 0 10px;
  line-height: 1.5;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(6px)'};
  transition: opacity 0.45s ease, transform 0.45s ease;
`;

export const DataAccessMockup = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.3 });
  const [activeTab, setActiveTab] = useState<AccessId>('dashboard');
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const onTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    let nextIndex: number | undefined;
    if (event.key === 'ArrowRight') {
      nextIndex = (index + 1) % ACCESS_TABS.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (index - 1 + ACCESS_TABS.length) % ACCESS_TABS.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = ACCESS_TABS.length - 1;
    }
    if (nextIndex === undefined) {
      return;
    }
    event.preventDefault();
    setActiveTab(ACCESS_TABS[nextIndex].id);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <ConceptPanel ref={ref}>
      <ConceptHeader>
        <Database size={14} />
        Your commerce data, wherever teams work
        <ConceptDots>
          <span />
          <span />
          <span />
        </ConceptDots>
      </ConceptHeader>
      <ConceptBody>
        <PanelIntro visible={inView} reduced={reduced}>
          The same numbers finance reconciles, available in the app, through
          Agent, exports, and the tools your team already uses.
        </PanelIntro>

        <MockTabBar
          role="tablist"
          aria-label="Data access surfaces"
          style={{ marginBottom: 10 }}
        >
          {ACCESS_TABS.map((tab, index) => {
            const Icon = tab.icon;
            return (
              <MockTab
                key={tab.id}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                id={`unify-access-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`unify-access-panel-${tab.id}`}
                tabIndex={activeTab === tab.id ? 0 : -1}
                $active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
              >
                <Icon size={14} strokeWidth={2} />
                {tab.label}
              </MockTab>
            );
          })}
        </MockTabBar>

        <PanelStack
          id={`unify-access-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`unify-access-tab-${activeTab}`}
        >
          {activeTab === 'dashboard' && (
            <DashboardPreview>
              <MiniMetric>
                <div className="label">Revenue</div>
                <div className="value">$4,287.60</div>
              </MiniMetric>
              <MiniMetric>
                <div className="label">Orders</div>
                <div className="value">312</div>
              </MiniMetric>
              <MiniMetric>
                <div className="label">EPC</div>
                <div className="value">$1.84</div>
              </MiniMetric>
              <TrendCell>
                <div className="label">Earnings · last 30 days</div>
                <svg
                  viewBox="0 0 260 34"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  {[
                    12,
                    18,
                    9,
                    22,
                    15,
                    26,
                    19,
                    14,
                    24,
                    17,
                    28,
                    21,
                    16,
                    25,
                    30,
                    20,
                    27,
                    23
                  ].map((height, index) => (
                    <rect
                      key={index}
                      x={index * 14.5 + 2}
                      y={34 - height}
                      width="8"
                      height={height}
                      rx="1.5"
                      fill={index % 4 === 2 ? '#b7dcf5' : '#4aa8e8'}
                    />
                  ))}
                </svg>
              </TrendCell>
            </DashboardPreview>
          )}

          {activeTab === 'agent' && (
            <ChatPreview>
              <AgentBadge>
                <Sparkles size={11} /> Affilimate Agent
              </AgentBadge>
              <Bubble $speaker="user">
                Which sponsorships are behind pace this quarter?
              </Bubble>
              <Bubble $speaker="agent">
                Two deals need attention. {DEMO_CAMPAIGNS.homeRefresh} is at 38%
                of goal with 6 days left in the flight.
              </Bubble>
            </ChatPreview>
          )}

          {activeTab === 'mcp' && (
            <McpPreview>
              <AgentBadge>
                <Plug size={11} /> Claude via Affilimate MCP
              </AgentBadge>
              <Bubble $speaker="user">
                Forecast revenue if our top merchant cuts rates from 10% to 8%.
              </Bubble>
              <Bubble $speaker="agent">
                You would lose about $12.4K a month based on the last 90 days,
                concentrated in 14 pages.
              </Bubble>
            </McpPreview>
          )}

          {activeTab === 'apis' && (
            <ApiPreview>
              {API_GROUPS.map((group) => {
                const Icon = group.icon;
                return (
                  <ApiGroup key={group.label}>
                    <ApiGroupLabel>{group.label}</ApiGroupLabel>
                    <ApiRow>
                      <Icon size={14} />
                      <span className="hint">{group.hint}</span>
                    </ApiRow>
                  </ApiGroup>
                );
              })}
            </ApiPreview>
          )}

          {activeTab === 'warehouse' && (
            <WarehousePreview>
              <AgentBadge>
                <Database size={11} /> Data warehouse
              </AgentBadge>
              <AccessSummary>
                <Database size={14} />
                Warehouse export · synced nightly
              </AccessSummary>
            </WarehousePreview>
          )}
        </PanelStack>
      </ConceptBody>
    </ConceptPanel>
  );
};
